import Box from '@mui/material/Box';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import Autocomplete, { AutocompleteRenderInputParams } from '@mui/material/Autocomplete';
import React, { useMemo, useCallback } from 'react';
import { ATTRIBUTE_MAPPING, ATTRIBUTE_ORDER, STUDY_KEY_TITLE } from '../constant';
import RadioGroup from '../RadioGroup';
import { Study, StudyAttribute, Indication, AttributeDefinition } from '../types';
import type { StudyKeyConfig } from '../types';
import IndicationComparisonActions from './IndicationComparisonActions';
import IndicationComparisonTitle from './IndicationComparisonTitle';
import IndicationSummaryTable from './IndicationSummaryTable';
import StudyComparisonBlock from './StudyComparisonBlock';
import styles from './styles';

const EMPTY = '—';
const SAMPLE_SIZE_ATTRIBUTE_KEY = 'samplesize';

function normalizeAttributeName(attributeName: string): string {
  return attributeName.replace(/\s/g, '').toLowerCase();
}

type StudyComparisonMatchType = 'identifier' | 'sample-size' | 'unmatched';

interface StudyComparisonSlot {
  slotKey: string;
  prevStudy: Study | null;
  currStudy: Study | null;
  matchType: StudyComparisonMatchType;
}

type ComparisonAttributeRow = {
  attribute_name: string;
  attribute_id: number;
  /** 0-based index among attributes with the same name/id (supports duplicate primary endpoints). */
  occurrenceIndex: number;
  rowKey: string;
};

function formatStudyValue(key: string, value: unknown): string {
  if (value == null || value === '') return EMPTY;
  const config = STUDY_KEY_TITLE[key];
  if (config?.options && (typeof value === 'boolean' || typeof value === 'string')) {
    const opt = config.options.find((o: string | { label: string; value: boolean | string }) =>
      typeof o === 'object' ? o.value === value : false
    );
    return (typeof opt === 'object' ? opt?.label : undefined) ?? String(value);
  }
  return String(value);
}

/* eslint-disable-next-line react/require-default-props */
interface EditableStudyKeyCellProps {
  fieldKey: string;
  value: unknown;
  config: StudyKeyConfig;
  studyId: string | number;
  // eslint-disable-next-line react/require-default-props, no-unused-vars
  onUpdateStudyField?: (key: string, value: string | number | boolean) => void;
}

const EditableStudyKeyCell = ({
  fieldKey,
  value,
  config,
  studyId,
  onUpdateStudyField
}: EditableStudyKeyCellProps): React.ReactElement => {
  const isBooleanRadio = Boolean(
    config.options?.some(
      (o): o is { label: string; value: boolean } =>
        typeof o === 'object' && typeof o.value === 'boolean'
    )
  );

  const handleRadioChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      if (isBooleanRadio) {
        onUpdateStudyField?.(fieldKey, e.target.value === 'true');
        return;
      }
      onUpdateStudyField?.(fieldKey, e.target.value);
    },
    [fieldKey, isBooleanRadio, onUpdateStudyField]
  );

  const handleAutocompleteChange = useCallback(
    (_e: React.SyntheticEvent, newValue: string) => {
      onUpdateStudyField?.(fieldKey, newValue ?? '');
    },
    [fieldKey, onUpdateStudyField]
  );

  const renderAutocompleteInput = useCallback(
    (params: AutocompleteRenderInputParams) => (
      <TextField
        id={params.id}
        fullWidth={params.fullWidth}
        size={params.size}
        type={config.inputType}
        placeholder={`Enter ${config.title}`}
        InputProps={{
          ...params.InputProps,
          inputProps: params.inputProps
        }}
        InputLabelProps={params.InputLabelProps}
        disabled={params.disabled}
      />
    ),
    [config.inputType, config.title]
  );

  const handleFieldChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      onUpdateStudyField?.(fieldKey, e.target.value);
    },
    [fieldKey, onUpdateStudyField]
  );

  if (config.inputType === 'radio-group' && config.options) {
    const options = config.options
      .filter((o): o is { label: string; value: boolean | string } => typeof o === 'object')
      .map(o => ({ label: o.label, value: String(o.value) }));
    const radioValue =
      value === true || value === false || typeof value === 'string' ? String(value) : '';
    return (
      <RadioGroup
        id={`edit-${studyId}-${fieldKey}`}
        value={radioValue}
        options={options}
        onChange={handleRadioChange}
      />
    );
  }

  if (config.inputType === 'text' || config.inputType === 'number') {
    const options = config.options || [];
    if (options.length > 0) {
      return (
        <Autocomplete
          freeSolo
          disableClearable
          size='small'
          fullWidth
          options={options}
          value={value ?? ''}
          onInputChange={handleAutocompleteChange}
          renderInput={renderAutocompleteInput}
        />
      );
    }
    return (
      <TextField
        size='small'
        fullWidth
        type={config.inputType}
        value={value ?? ''}
        onChange={handleFieldChange}
        placeholder={`Enter ${config.title}`}
      />
    );
  }

  return <>{formatStudyValue(fieldKey, value)}</>;
};

interface EditableAttributeCellProps {
  attrName: string;
  attrId: number;
  occurrenceIndex: number;
  value: string;
  description: string;
  descriptionEnabled: boolean;
  // eslint-disable-next-line react/require-default-props
  onUpdateStudyAttribute?: (
    // eslint-disable-next-line no-unused-vars
    attributeName: string,
    // eslint-disable-next-line no-unused-vars
    attributeId: number,
    // eslint-disable-next-line no-unused-vars
    value: string,
    // eslint-disable-next-line no-unused-vars
    field?: 'value' | 'description',
    // eslint-disable-next-line no-unused-vars
    occurrenceIndex?: number
  ) => void;
}

const EditableAttributeCell = ({
  attrName,
  attrId,
  occurrenceIndex,
  value,
  description,
  descriptionEnabled,
  onUpdateStudyAttribute
}: EditableAttributeCellProps) => {
  const handleAttributeChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      onUpdateStudyAttribute?.(attrName, attrId, e.target.value, 'value', occurrenceIndex);
    },
    [attrName, attrId, occurrenceIndex, onUpdateStudyAttribute]
  );
  const handleDescriptionChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      onUpdateStudyAttribute?.(attrName, attrId, e.target.value, 'description', occurrenceIndex);
    },
    [attrName, attrId, occurrenceIndex, onUpdateStudyAttribute]
  );
  return (
    <Box>
      <TextField size='small' fullWidth value={value} onChange={handleAttributeChange} />
      {descriptionEnabled && (
        <TextField
          size='small'
          fullWidth
          multiline
          maxRows={4}
          sx={{ mt: 1 }}
          label='Description'
          value={description}
          onChange={handleDescriptionChange}
          placeholder={`${attrName} Description`}
        />
      )}
    </Box>
  );
};

function attributeMatches(
  attr: StudyAttribute,
  attributeName: string,
  attributeId?: number
): boolean {
  if (attributeId != null && Number(attr.attribute_id) === Number(attributeId)) {
    return true;
  }
  return Boolean(attributeName) && attr.attribute_name === attributeName;
}

function findStudyAttribute(
  study: Study | null,
  attributeName: string,
  attributeId?: number,
  occurrenceIndex = 0
): StudyAttribute | undefined {
  if (!study?.attributes) return undefined;
  const matches = study.attributes.filter((a: StudyAttribute) =>
    attributeMatches(a, attributeName, attributeId)
  );
  return matches[occurrenceIndex];
}

function getAttributeValue(
  study: Study | null,
  attributeName: string,
  attributeId?: number,
  occurrenceIndex = 0
): string {
  const val = findStudyAttribute(
    study,
    attributeName,
    attributeId,
    occurrenceIndex
  )?.attribute_value;
  return val != null && val !== '' ? String(val) : EMPTY;
}

function getAttributeDescription(
  study: Study | null,
  attributeName: string,
  attributeId?: number,
  occurrenceIndex = 0
): string {
  const val = findStudyAttribute(
    study,
    attributeName,
    attributeId,
    occurrenceIndex
  )?.attribute_description;
  return val != null && val !== '' ? String(val) : EMPTY;
}

function isDescriptionEnabledAttribute(attributeName: string): boolean {
  return Boolean(ATTRIBUTE_MAPPING[normalizeAttributeName(attributeName)]?.descriptionEnabled);
}

function renderAttributeDisplay(
  study: Study | null,
  attributeName: string,
  attributeId?: number,
  occurrenceIndex = 0
): React.ReactNode {
  const value = getAttributeValue(study, attributeName, attributeId, occurrenceIndex);
  if (!isDescriptionEnabledAttribute(attributeName)) {
    return value;
  }
  const description = getAttributeDescription(study, attributeName, attributeId, occurrenceIndex);
  return (
    <Box>
      <Box>{value}</Box>
      <Box mt={0.5}>
        <Typography component='span' variant='caption' fontWeight={700}>
          Description:{' '}
        </Typography>
        <Typography component='span' variant='body2'>
          {description}
        </Typography>
      </Box>
    </Box>
  );
}

const STUDY_KEYS_ORDER = ATTRIBUTE_ORDER.filter((k: string) => STUDY_KEY_TITLE[k]);

function getStudyKey(
  study: Study | null,
  identifierAttributeId: number | null | undefined,
  nctIdAttributeId: number | null | undefined
): string | number | null {
  if (!study) return null;
  const attrs = study.attributes ?? [];
  if (identifierAttributeId != null) {
    const identifierAttr = attrs.find(
      (a: StudyAttribute) => Number(a.attribute_id) === identifierAttributeId
    );
    const identifierVal = identifierAttr?.attribute_value;
    if (identifierVal != null && identifierVal !== '') {
      return String(identifierVal);
    }
  }
  if (nctIdAttributeId != null) {
    const nctAttr = attrs.find((a: StudyAttribute) => Number(a.attribute_id) === nctIdAttributeId);
    const nctVal = nctAttr?.attribute_value;
    if (nctVal != null && nctVal !== '') {
      return String(nctVal);
    }
  }
  return study.id ?? null;
}

function normalizeSampleSizeValue(value: unknown): string | null {
  if (value == null || value === '') return null;
  const rawValue = String(value).trim();
  if (!rawValue) return null;
  const numericValue = rawValue.replace(/,/g, '');
  if (/^\d+(\.\d+)?$/.test(numericValue)) {
    return String(Number(numericValue));
  }
  return rawValue.toLowerCase();
}

function getSampleSizeKey(study: Study | null): string | null {
  const sampleSizeAttr = study?.attributes?.find(
    (a: StudyAttribute) => normalizeAttributeName(a.attribute_name) === SAMPLE_SIZE_ATTRIBUTE_KEY
  );
  return normalizeSampleSizeValue(sampleSizeAttr?.attribute_value);
}

function getStudySortKey(
  study: Study,
  identifierAttributeId: number | null | undefined,
  nctIdAttributeId: number | null | undefined
): string {
  const studyKey = getStudyKey(study, identifierAttributeId, nctIdAttributeId);
  return String(studyKey ?? getSampleSizeKey(study) ?? study.id);
}

function makeSlotKey(
  prefix: string,
  prevStudy: Study | null,
  currStudy: Study | null,
  fallbackKey: string
): string {
  return [prefix, fallbackKey, prevStudy?.id ?? 'none', currStudy?.id ?? 'none'].join(':');
}

function resolveAttributeName(
  attr: StudyAttribute,
  attributeOptions: AttributeDefinition[]
): string | null {
  if (attr.attribute_name) return attr.attribute_name;
  if (attr.attribute_id == null) return null;
  const match = attributeOptions.find(
    (option: AttributeDefinition) => Number(option.attribute_id) === Number(attr.attribute_id)
  );
  return match?.attribute_name ?? null;
}

function getAttributeGroupKey(
  attr: StudyAttribute,
  attributeOptions: AttributeDefinition[]
): { key: string; attributeName: string; attributeId: number } | null {
  if (attr.attribute_id == null) return null;
  const attributeId = Number(attr.attribute_id);
  const attributeName = resolveAttributeName(attr, attributeOptions);
  const key = attributeName ? `name:${attributeName}` : `id:${attributeId}`;
  return {
    key,
    attributeName: attributeName ?? `Attribute ${attributeId}`,
    attributeId
  };
}

function countAttributeOccurrences(
  study: Study | null,
  attributeOptions: AttributeDefinition[]
): Map<string, { attributeName: string; attributeId: number; count: number }> {
  const counts = new Map<string, { attributeName: string; attributeId: number; count: number }>();
  (study?.attributes ?? []).forEach((attr: StudyAttribute) => {
    const resolved = getAttributeGroupKey(attr, attributeOptions);
    if (!resolved) return;
    const existing = counts.get(resolved.key);
    if (existing) {
      existing.count += 1;
      return;
    }
    counts.set(resolved.key, {
      attributeName: resolved.attributeName,
      attributeId: resolved.attributeId,
      count: 1
    });
  });
  return counts;
}

function getAttributesForStudy(
  prevStudy: Study | null,
  currStudy: Study | null,
  attributeOptions: AttributeDefinition[] = []
): ComparisonAttributeRow[] {
  // Keep duplicate attributes (e.g. two Primary Endpoints) as separate rows,
  // aligned by occurrence order between previous and current studies.
  const prevCounts = countAttributeOccurrences(prevStudy, attributeOptions);
  const currCounts = countAttributeOccurrences(currStudy, attributeOptions);

  const orderedKeys: string[] = [];
  const seenKeys = new Set<string>();
  const appendKeysFromStudy = (study: Study | null) => {
    (study?.attributes ?? []).forEach((attr: StudyAttribute) => {
      const resolved = getAttributeGroupKey(attr, attributeOptions);
      if (!resolved || seenKeys.has(resolved.key)) return;
      seenKeys.add(resolved.key);
      orderedKeys.push(resolved.key);
    });
  };
  appendKeysFromStudy(currStudy);
  appendKeysFromStudy(prevStudy);

  const rows: ComparisonAttributeRow[] = [];
  orderedKeys.forEach(key => {
    const meta = currCounts.get(key) ?? prevCounts.get(key);
    if (!meta) return;
    const occurrenceCount = Math.max(
      prevCounts.get(key)?.count ?? 0,
      currCounts.get(key)?.count ?? 0
    );
    for (let occurrenceIndex = 0; occurrenceIndex < occurrenceCount; occurrenceIndex += 1) {
      rows.push({
        attribute_name: meta.attributeName,
        attribute_id: meta.attributeId,
        occurrenceIndex,
        rowKey: `${key}:${occurrenceIndex}`
      });
    }
  });
  return rows;
}

function getStudySlotsForComparison(
  prevStudies: Study[],
  currStudies: Study[],
  identifierAttributeId: number | null | undefined,
  nctIdAttributeId: number | null | undefined
): StudyComparisonSlot[] {
  const prevList = prevStudies ?? [];
  const currList = currStudies ?? [];
  const slots: StudyComparisonSlot[] = [];
  const matchedPrevIndexes = new Set<number>();
  const matchedCurrIndexes = new Set<number>();
  const currIndexesByStudyKey = new Map<string, number[]>();

  currList.forEach((study: Study, index: number) => {
    const studyKey = getStudyKey(study, identifierAttributeId, nctIdAttributeId);
    if (studyKey == null) return;
    const key = String(studyKey);
    const existing = currIndexesByStudyKey.get(key) ?? [];
    currIndexesByStudyKey.set(key, [...existing, index]);
  });

  prevList
    .map((study: Study, index: number) => ({
      study,
      index,
      studyKey: getStudyKey(study, identifierAttributeId, nctIdAttributeId)
    }))
    .filter(({ studyKey }) => studyKey != null)
    .sort((a, b) =>
      String(a.studyKey).localeCompare(String(b.studyKey), undefined, { numeric: true })
    )
    .forEach(({ study, index, studyKey }) => {
      const key = String(studyKey);
      const currIndexes = currIndexesByStudyKey.get(key) ?? [];
      const currIndex = currIndexes.find(candidateIndex => !matchedCurrIndexes.has(candidateIndex));
      if (currIndex == null) return;
      const currStudy = currList[currIndex];
      matchedPrevIndexes.add(index);
      matchedCurrIndexes.add(currIndex);
      slots.push({
        slotKey: makeSlotKey('identifier', study, currStudy, key),
        prevStudy: study,
        currStudy,
        matchType: 'identifier'
      });
    });

  prevList.forEach((study: Study, index: number) => {
    if (matchedPrevIndexes.has(index)) return;
    const sampleSizeKey = getSampleSizeKey(study);
    if (sampleSizeKey == null) return;
    const currIndex = currList.findIndex(
      (currStudy: Study, candidateIndex: number) =>
        !matchedCurrIndexes.has(candidateIndex) && getSampleSizeKey(currStudy) === sampleSizeKey
    );
    if (currIndex < 0) return;
    const currStudy = currList[currIndex];
    matchedPrevIndexes.add(index);
    matchedCurrIndexes.add(currIndex);
    slots.push({
      slotKey: makeSlotKey('sample-size', study, currStudy, sampleSizeKey),
      prevStudy: study,
      currStudy,
      matchType: 'sample-size'
    });
  });

  const previousOnly = prevList
    .map((study: Study, index: number) => ({ study, index }))
    .filter(({ index }) => !matchedPrevIndexes.has(index))
    .sort((a, b) =>
      getStudySortKey(a.study, identifierAttributeId, nctIdAttributeId).localeCompare(
        getStudySortKey(b.study, identifierAttributeId, nctIdAttributeId),
        undefined,
        { numeric: true }
      )
    );

  const currentOnly = currList
    .map((study: Study, index: number) => ({ study, index }))
    .filter(({ index }) => !matchedCurrIndexes.has(index))
    .sort((a, b) =>
      getStudySortKey(a.study, identifierAttributeId, nctIdAttributeId).localeCompare(
        getStudySortKey(b.study, identifierAttributeId, nctIdAttributeId),
        undefined,
        { numeric: true }
      )
    );

  previousOnly.forEach(({ study }) =>
    slots.push({
      slotKey: makeSlotKey(
        'previous-only',
        study,
        null,
        getStudySortKey(study, identifierAttributeId, nctIdAttributeId)
      ),
      prevStudy: study,
      currStudy: null,
      matchType: 'unmatched'
    })
  );

  currentOnly.forEach(({ study }) =>
    slots.push({
      slotKey: makeSlotKey(
        'current-only',
        null,
        study,
        getStudySortKey(study, identifierAttributeId, nctIdAttributeId)
      ),
      prevStudy: null,
      currStudy: study,
      matchType: 'unmatched'
    })
  );

  return slots;
}

/* eslint-disable react/require-default-props */
interface IndicationComparisonProps {
  title: string;
  onCopy?: () => void;
  onAcceptCurrent?: () => void;
  onCopyIndication?: () => void;
  // eslint-disable-next-line no-unused-vars
  onCopyStudy?: (prevStudy: Study) => void;
  parentIndication: Indication | null;
  currentIndication: Indication | null;
  nctIdAttributeId: number | null;
  identifierAttributeId: number | null;
  // eslint-disable-next-line react/require-default-props
  attributeOptions?: AttributeDefinition[];
  showActions?: boolean;
  isCopying?: boolean;
  copyingStudyId?: string | number | null;
  editingStudyId?: number | string | null;
  editingStudyData?: Study | null;
  // eslint-disable-next-line no-unused-vars
  onEditStudy?: (study: Study) => void;
  onSaveStudy?: () => void;
  // eslint-disable-next-line no-unused-vars
  onUpdateStudyField?: (key: string, value: string | number | boolean) => void;
  onUpdateStudyAttribute?: (
    // eslint-disable-next-line no-unused-vars
    attributeName: string,
    // eslint-disable-next-line no-unused-vars
    attributeId: number,
    // eslint-disable-next-line no-unused-vars
    value: string,
    // eslint-disable-next-line no-unused-vars
    field?: 'value' | 'description',
    // eslint-disable-next-line no-unused-vars
    occurrenceIndex?: number
  ) => void;
}
/* eslint-enable react/require-default-props */

const IndicationComparison = ({
  title,
  parentIndication,
  currentIndication,
  nctIdAttributeId,
  identifierAttributeId,
  attributeOptions = [],
  onCopy,
  onAcceptCurrent,
  onCopyIndication,
  onCopyStudy,
  showActions = true,
  isCopying = false,
  copyingStudyId = null,
  editingStudyId = null,
  editingStudyData = null,
  onEditStudy,
  onSaveStudy,
  onUpdateStudyField,
  onUpdateStudyAttribute
}: IndicationComparisonProps) => {
  const prevIndication = parentIndication ?? null;
  const currIndication = currentIndication ?? null;

  const prevStudies = prevIndication?.studies ?? [];
  const currStudies = currIndication?.studies ?? [];

  const studySlots = useMemo(
    () =>
      getStudySlotsForComparison(prevStudies, currStudies, identifierAttributeId, nctIdAttributeId),
    [prevStudies, currStudies, identifierAttributeId, nctIdAttributeId]
  );

  const prevStudyBySlotKey = useMemo(
    () =>
      new Map<string, Study>(
        studySlots
          .filter(
            (slot): slot is StudyComparisonSlot & { prevStudy: Study } => slot.prevStudy != null
          )
          .map(slot => [slot.slotKey, slot.prevStudy] as [string, Study])
      ),
    [studySlots]
  );

  const currStudyBySlotKey = useMemo(
    () =>
      new Map<string, Study>(
        studySlots
          .filter(
            (slot): slot is StudyComparisonSlot & { currStudy: Study } => slot.currStudy != null
          )
          .map(slot => [slot.slotKey, slot.currStudy] as [string, Study])
      ),
    [studySlots]
  );

  const handleCopyStudyClick = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      const raw = (e.currentTarget as HTMLButtonElement).dataset.slotKey;
      if (raw == null) return;
      const prevStudy = prevStudyBySlotKey.get(raw);
      if (prevStudy) onCopyStudy?.(prevStudy);
    },
    [prevStudyBySlotKey, onCopyStudy]
  );

  const handleEditStudyClick = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      const raw = (e.currentTarget as HTMLButtonElement).dataset.slotKey;
      if (raw == null) return;
      const currStudy = currStudyBySlotKey.get(raw);
      if (currStudy) onEditStudy?.(currStudy);
    },
    [currStudyBySlotKey, onEditStudy]
  );

  const renderCurrentCellForStudyKey = (
    key: string,
    currStudy: Study | null,
    studyId: string | number
  ) => {
    const isEditing =
      editingStudyId != null && currStudy?.id === editingStudyId && editingStudyData != null;
    const displayStudy = isEditing ? editingStudyData : currStudy;
    const value = displayStudy?.[key];

    if (!isEditing) {
      return formatStudyValue(key, value);
    }

    const config = STUDY_KEY_TITLE[key];
    if (!config) return formatStudyValue(key, value);

    return (
      <EditableStudyKeyCell
        fieldKey={key}
        value={value}
        config={config}
        studyId={studyId}
        onUpdateStudyField={onUpdateStudyField}
      />
    );
  };

  const renderCurrentCellForAttribute = (
    attrName: string,
    attrId: number,
    occurrenceIndex: number,
    currStudy: Study | null
  ) => {
    const isEditing =
      editingStudyId != null && currStudy?.id === editingStudyId && editingStudyData != null;
    if (!isEditing) {
      return renderAttributeDisplay(currStudy, attrName, attrId, occurrenceIndex);
    }
    const attr = findStudyAttribute(editingStudyData, attrName, attrId, occurrenceIndex);
    const valueStr = String(attr?.attribute_value ?? '');
    const descriptionStr = String(attr?.attribute_description ?? '');

    return (
      <EditableAttributeCell
        attrName={attrName}
        attrId={attrId}
        occurrenceIndex={occurrenceIndex}
        value={valueStr}
        description={descriptionStr}
        descriptionEnabled={isDescriptionEnabledAttribute(attrName)}
        onUpdateStudyAttribute={onUpdateStudyAttribute}
      />
    );
  };

  const hasAnyAction = Boolean(onCopy || onAcceptCurrent || (!currIndication && onCopyIndication));

  return (
    <Box height='100%' display='flex' flexDirection='column'>
      <IndicationComparisonTitle title={title} />

      <Box sx={styles.content}>
        <IndicationSummaryTable
          prevText={prevIndication?.text ?? null}
          currText={currIndication?.text ?? null}
          emptyLabel={EMPTY}
        />

        {studySlots.map((slot: StudyComparisonSlot, displayIndex: number) => {
          const { slotKey, prevStudy, currStudy, matchType } = slot;
          const canCopyStudy = currStudy == null && prevStudy != null && onCopyStudy != null;
          const studyCopying = prevStudy != null && copyingStudyId === prevStudy.id;
          const rowStudyId = currStudy?.id ?? slotKey;
          const matchLabel = matchType === 'sample-size' ? 'Matched by sample size' : undefined;

          return (
            <StudyComparisonBlock
              key={`study-${slotKey}`}
              slotKey={slotKey}
              displayIndex={displayIndex}
              matchLabel={matchLabel}
              prevStudy={prevStudy}
              currStudy={currStudy}
              rowStudyId={rowStudyId}
              canCopyStudy={canCopyStudy}
              studyCopying={studyCopying}
              isCopying={isCopying}
              onCopyStudyClick={handleCopyStudyClick}
              onEditStudyClick={onEditStudy != null ? handleEditStudyClick : undefined}
              onSaveStudy={onSaveStudy}
              editingStudyId={editingStudyId ?? null}
              editingStudyData={
                editingStudyId != null && currStudy?.id === editingStudyId ? editingStudyData : null
              }
              studyKeysOrder={STUDY_KEYS_ORDER}
              attributes={getAttributesForStudy(prevStudy, currStudy, attributeOptions)}
              formatStudyValue={formatStudyValue}
              getAttributeValue={renderAttributeDisplay}
              renderCurrentCellForStudyKey={renderCurrentCellForStudyKey}
              renderCurrentCellForAttribute={renderCurrentCellForAttribute}
            />
          );
        })}
      </Box>

      <IndicationComparisonActions
        showActions={showActions}
        hasAnyAction={hasAnyAction}
        currIndication={currIndication}
        isCopying={isCopying}
        onCopyIndication={onCopyIndication}
        onCopy={onCopy}
        onAcceptCurrent={onAcceptCurrent}
      />
    </Box>
  );
};

export default React.memo(IndicationComparison);
